// =============================================================================
// SIREPPAR - Bandeja de solicitudes de constitución de alianza (Card D / U3)
// =============================================================================
// Bandeja PROPIA, separada de /gestion. Un expediente de alianza no se lee con
// las columnas del resto: lo que importa de un vistazo es QUIÉNES la integran y
// cuántos de esos integrantes no están en SIREPPAR (los que exigen verificación
// manual antes de poder elevar). Eso no entra en la tabla genérica.
//
// El botón principal cambia con el estado: PRESENTADO abre "Iniciar" (aprueba la
// admisibilidad en bloque y pasa a análisis, ver iniciarAnalisisAlianza en el
// backend); de ahí en adelante es "Gestionar" y lleva al detalle del expediente,
// que es la MISMA pantalla que el resto de los trámites (ExpedienteDetailPage).
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { alianzaGestionService, api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// -----------------------------------------------------------------------------
// Tipos (espejan la respuesta de listarExpedientesAlianza)
// -----------------------------------------------------------------------------

interface IntegranteResumen {
  id: string;
  denominacion: string | null;
  enSireppar: boolean;
}

interface ExpedienteAlianza {
  id: string;
  numero: string;
  estado: string;
  estadoLabel: string;
  fechaPresentacion: string | null;
  fechaCreacion: string;
  fechaVencimiento: string | null;
  analista?: { id: string; nombre: string; apellido: string } | null;
  alianza: { id: string; denominacion: string; sigla?: string | null; tipo?: string };
  convocatoria: { id: string; denominacion: string; codigoCorto: string; fechaActo: string } | null;
  integrantes: {
    total: number;
    otros: number;
    distritales: number;
    sinVerificar: number;
    nombres: IntegranteResumen[];
  };
  observaciones: { total: number; activas: number; respondidas: number };
}

interface Contadores {
  presentadas: number;
  enAnalisis: number;
  observadas: number;
  enSecretario: number;
  // Tarjeta propia y no sumada a la del Secretario: una vez elevado, el expediente
  // está esperando al Tribunal y la casa no tiene nada que hacer con él. Contándolo
  // en "En Secretario" aparecía como trabajo pendiente propio.
  enTep: number;
  reconocidas: number;
  rechazadas: number;
}

const ESTADO_BADGE: Record<string, string> = {
  PRESENTADO: 'bg-blue-100 text-blue-800',
  EN_ADMISIBILIDAD: 'bg-purple-100 text-purple-800',
  EN_ANALISIS: 'bg-yellow-100 text-yellow-800',
  OBSERVADO: 'bg-orange-100 text-orange-800',
  EN_SECRETARIO: 'bg-indigo-100 text-indigo-800',
  PENDIENTE_RESOLUCION: 'bg-indigo-100 text-indigo-800',
  // Mismo cian que usa EN_TEP en el resto del sistema (ver estadoConfig en
  // ExpedienteDetailPage): el estado se lee igual desde cualquier pantalla.
  EN_TEP: 'bg-cyan-100 text-cyan-800',
  // Se conserva sólo para los expedientes viejos: en alianzas el circuito de "no
  // presentada" ya no existe y no hay camino nuevo que lleve a ese estado. Sin el
  // badge quedarían sin color en la tabla.
  NO_PRESENTADO: 'bg-red-100 text-red-800',
  VIGENTE: 'bg-green-100 text-green-800',
  RECHAZADO: 'bg-red-100 text-red-800',
};

const OPCIONES_ESTADO = [
  { value: '', label: 'Todos los estados' },
  { value: 'PRESENTADO', label: 'Presentada' },
  { value: 'EN_ANALISIS', label: 'En análisis' },
  { value: 'OBSERVADO', label: 'Observada' },
  { value: 'EN_SECRETARIO', label: 'En Secretario' },
  { value: 'EN_TEP', label: 'En el TEP' },
  // SIN "No presentada": ese circuito ya no existe en alianzas. Los expedientes
  // viejos que quedaron en ese estado siguen apareciendo con "Todos los estados".
  { value: 'VIGENTE', label: 'Reconocida' },
  { value: 'RECHAZADO', label: 'Rechazada' },
];

const ROLES_ACCION = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'];

const fmtFecha = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const diasDesde = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)));

export default function BandejaAlianzasPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const puedeAccionar = ROLES_ACCION.includes(user?.rol || '');

  const [convocatoriaId, setConvocatoriaId] = useState('');
  const [estado, setEstado] = useState('');
  const [analistaId, setAnalistaId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);
  // Modal de asignación. La query de opciones es lazy: se dispara sólo con el
  // modal abierto (mismo patrón que el modal de asignar analista del expediente).
  const [modalAsignar, setModalAsignar] = useState<ExpedienteAlianza | null>(null);
  const [analistaElegido, setAnalistaElegido] = useState('');

  const filtros = { convocatoriaId, estado, analistaId, busqueda };

  const { data, isLoading } = useQuery({
    queryKey: ['bandeja-alianzas', filtros],
    queryFn: () => alianzaGestionService.bandeja(filtros),
  });

  const { data: contadores } = useQuery<Contadores>({
    queryKey: ['bandeja-alianzas-contadores', convocatoriaId],
    queryFn: () => alianzaGestionService.contadores(convocatoriaId || undefined),
  });

  const { data: analistas } = useQuery({
    queryKey: ['alianzas-analistas'],
    queryFn: () => alianzaGestionService.analistas(),
  });

  const { data: convocatorias } = useQuery({
    queryKey: ['convocatorias-alianzas'],
    queryFn: async () => {
      const res = await api.get('/alianzas/convocatorias-abiertas');
      return res.data.data as Array<{ id: string; denominacion: string; codigoCorto: string }>;
    },
  });

  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas'] });
    queryClient.invalidateQueries({ queryKey: ['bandeja-alianzas-contadores'] });
  };

  const iniciarMutation = useMutation({
    mutationFn: (id: string) => alianzaGestionService.iniciarAnalisis(id),
    onSuccess: () => {
      toast.success('Admisibilidad aprobada — la alianza pasó a análisis');
      refrescar();
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo iniciar el análisis'),
  });

  const asignarMutation = useMutation({
    mutationFn: ({ id, analista }: { id: string; analista: string }) =>
      alianzaGestionService.asignar(id, analista),
    onSuccess: () => {
      toast.success('Analista asignado');
      setModalAsignar(null);
      setAnalistaElegido('');
      refrescar();
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo asignar'),
  });

  const items: ExpedienteAlianza[] = data?.items ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Alianzas Electorales</h1>
        <p className="text-gray-500 mt-1">
          Solicitudes de constitución de alianza — verificación de la SEP, elevación del Secretario
          y resolución del TEP (Arts. 26 y 27, Ley XII N° 23)
        </p>
      </div>

      {/* Recordatorio del plazo. No es decorativo: si la alianza no queda reconocida
          antes del cierre, las agrupaciones no pueden oficializar lista. */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          Las alianzas se solicitan con anticipación al acto electoral (Art. 26). Sin alianza
          reconocida antes del cierre, la agrupación no puede oficializar lista de candidatos.
        </p>
      </div>

      {/* Contadores */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {[
          { label: 'Presentadas', valor: contadores?.presentadas, color: 'border-blue-500' },
          { label: 'En análisis', valor: contadores?.enAnalisis, color: 'border-yellow-500' },
          { label: 'Observadas', valor: contadores?.observadas, color: 'border-orange-500' },
          { label: 'En Secretario', valor: contadores?.enSecretario, color: 'border-indigo-500' },
          { label: 'En el TEP', valor: contadores?.enTep, color: 'border-cyan-500' },
          { label: 'Reconocidas', valor: contadores?.reconocidas, color: 'border-green-500' },
          { label: 'Rechazadas', valor: contadores?.rechazadas, color: 'border-red-500' },
        ].map((c) => (
          <div key={c.label} className={`bg-white rounded-lg shadow p-4 border-l-4 ${c.color}`}>
            <p className="text-sm text-gray-500">{c.label}</p>
            <p className="text-2xl font-bold">{c.valor ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Convocatoria:</label>
          <select
            value={convocatoriaId}
            onChange={(e) => setConvocatoriaId(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas las convocatorias</option>
            {(convocatorias ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.denominacion}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Estado:</label>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {OPCIONES_ESTADO.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Analista:</label>
          <select
            value={analistaId}
            onChange={(e) => setAnalistaId(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            {(analistas ?? []).map((a: any) => (
              <option key={a.id} value={a.id}>{a.apellido}, {a.nombre}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <label className="text-sm text-gray-600 font-medium">Buscar:</label>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="N° de expediente, alianza o partido integrante"
            className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay solicitudes de alianza con esos filtros</div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-56">Alianza</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partidos integrantes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Convocatoria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Presentada</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((exp) => {
                const sinIniciar = exp.estado === 'PRESENTADO' || exp.estado === 'EN_ADMISIBILIDAD';
                return (
                  <tr key={exp.id} className={exp.observaciones.respondidas > 0 && exp.estado === 'OBSERVADO' ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 align-top">
                      <Link to={`/expedientes/${exp.id}`} className="text-blue-600 hover:underline font-medium">
                        {exp.numero}
                      </Link>
                      <p className="text-xs text-gray-500">
                        {exp.analista ? `${exp.analista.apellido}, ${exp.analista.nombre}` : 'Sin analista'}
                      </p>
                    </td>
                    <td className="px-4 py-3 align-top w-56 max-w-[14rem]">
                      <p className="text-sm font-medium break-words">{exp.alianza.denominacion}</p>
                      {exp.alianza.sigla && (
                        <p className="text-xs text-gray-500">Sigla {exp.alianza.sigla}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <ul className="text-sm text-gray-700 space-y-0.5">
                        {exp.integrantes.nombres
                          .slice(0, expandido === exp.id ? undefined : 2)
                          .map((i) => (
                            <li key={i.id} className={i.enSireppar ? '' : 'text-gray-500 italic'}>
                              {i.denominacion}
                            </li>
                          ))}
                      </ul>
                      {exp.integrantes.nombres.length > 2 && (
                        <button
                          onClick={() => setExpandido(expandido === exp.id ? null : exp.id)}
                          className="text-xs text-blue-600 hover:underline mt-0.5"
                        >
                          {expandido === exp.id
                            ? 'Ver menos'
                            : `+ ${exp.integrantes.nombres.length - 2} más`}
                        </button>
                      )}
                      {/* Los integrantes fuera de SIREPPAR son los que traban la
                          elevación mientras no se verifiquen a mano. */}
                      {exp.integrantes.sinVerificar > 0 && (
                        <p className="text-xs text-amber-700 font-semibold mt-1">
                          {exp.integrantes.sinVerificar} sin verificar
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                      {exp.convocatoria?.denominacion ?? '—'}
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700 whitespace-nowrap">
                      {fmtFecha(exp.fechaPresentacion ?? exp.fechaCreacion)}
                      {sinIniciar && (
                        <p className="text-xs text-gray-500">
                          {diasDesde(exp.fechaPresentacion ?? exp.fechaCreacion)} días sin iniciar
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`px-2 py-1 text-xs rounded-full ${ESTADO_BADGE[exp.estado] || 'bg-gray-100 text-gray-700'}`}>
                        {exp.estadoLabel}
                      </span>
                      {exp.observaciones.activas > 0 && (
                        <p className="text-xs text-orange-700 font-semibold mt-1">
                          {exp.observaciones.activas} observación{exp.observaciones.activas === 1 ? '' : 'es'} abierta{exp.observaciones.activas === 1 ? '' : 's'}
                        </p>
                      )}
                      {exp.observaciones.respondidas > 0 && (
                        <p className="text-xs text-green-700 font-semibold mt-0.5">
                          {exp.observaciones.respondidas} respondida{exp.observaciones.respondidas === 1 ? '' : 's'}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                        {puedeAccionar && (
                          <button
                            onClick={() => { setModalAsignar(exp); setAnalistaElegido(exp.analista?.id || ''); }}
                            className="px-2 py-1 text-gray-600 text-sm hover:bg-gray-100 rounded"
                          >
                            Asignar
                          </button>
                        )}
                        {puedeAccionar && sinIniciar ? (
                          <button
                            onClick={() => iniciarMutation.mutate(exp.id)}
                            disabled={iniciarMutation.isPending}
                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                            title="Aprueba la admisibilidad de toda la documentación y pasa el expediente a análisis"
                          >
                            Iniciar
                          </button>
                        ) : (
                          <Link
                            to={`/expedientes/${exp.id}`}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Gestionar
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Modal: asignar analista */}
      {modalAsignar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-1">Asignar analista</h3>
            <p className="text-sm text-gray-600 mb-4">
              Expediente {modalAsignar.numero} — {modalAsignar.alianza.denominacion}
            </p>
            <select
              value={analistaElegido}
              onChange={(e) => setAnalistaElegido(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccione un analista</option>
              {(analistas ?? []).map((a: any) => (
                <option key={a.id} value={a.id}>{a.apellido}, {a.nombre} ({a.rol})</option>
              ))}
            </select>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => { setModalAsignar(null); setAnalistaElegido(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => asignarMutation.mutate({ id: modalAsignar.id, analista: analistaElegido })}
                disabled={!analistaElegido || asignarMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {asignarMutation.isPending ? 'Asignando...' : 'Asignar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
